import classnames from 'classnames';
import React, { useRef } from 'react';

import * as ButtonEnums from './ButtonEnums';

interface ButtonProps {
	/** What is inside the button, can be text or react component */
	children?: React.ReactNode;
	/** Callback to be called when the button is clicked  */
	onClick: (e: React.MouseEvent) => void;
	/** Button size  */
	size?: ButtonEnums.size;
	/** Whether the button should be disabled  */
	disabled?: boolean;
	/** Button type  */
	type?: ButtonEnums.type;
	/** Button name */
	name?: string;
	/** Button start icon name - if any icon is specified  */
	startIcon?: React.ReactNode;
	/** Button end icon name - if any icon is specified  */
	endIcon?: React.ReactNode;
	/** Additional TailwindCSS classnames */
	className?: string;
}

const sizeClasses = {
	[ButtonEnums.size.small]: 'h-[24px] text-[14px]',
	[ButtonEnums.size.medium]: 'h-[30px] text-[16px]',
};

const layoutClasses =
	'box-content inline-flex flex-row items-center justify-center gap-[5px] justify center px-[10px] outline-none rounded';

const baseFontTextClasses = 'leading-[1.2] font-sans text-center whitespace-nowrap';

const fontTextClasses = {
	[ButtonEnums.type.primary]: classnames(baseFontTextClasses, 'text-sm'),
	[ButtonEnums.type.secondary]: classnames(baseFontTextClasses, 'font-400'),
};

const baseEnabledEffectClasses = 'transition duration-300 ease-in-out focus:outline-none';

const enabledEffectClasses = {
	[ButtonEnums.type.primary]: classnames(baseEnabledEffectClasses, 'hover:bg-primary-active'),
	[ButtonEnums.type.secondary]: classnames(baseEnabledEffectClasses, 'hover:bg-customblue-50 active:bg-customblue-20'),
};

const baseEnabledClasses = 'text-common-light';

const enabledClasses = {
	[ButtonEnums.type.primary]: classnames('bg-primary-light', baseEnabledClasses, enabledEffectClasses[ButtonEnums.type.primary]),
	[ButtonEnums.type.secondary]: classnames('bg-customblue-30', baseEnabledClasses, enabledEffectClasses[ButtonEnums.type.secondary]),
};

const disabledClasses = 'bg-inputfield-placeholder text-common-light cursor-default';

const defaults = {
	color: 'default',
	disabled: false,
	rounded: 'small',
	size: ButtonEnums.size.small,
	type: ButtonEnums.type.primary,
};

const Button = ({
	children = null,
	size = defaults.size,
	disabled = defaults.disabled,
	type = defaults.type,
	startIcon: startIconProp,
	endIcon: endIconProp,
	name,
	className,
	onClick = (_e) => undefined,
	...rest
}: ButtonProps) => {
	const startIcon = startIconProp && React.isValidElement(startIconProp) && (
		<>
			{React.cloneElement(startIconProp, {
				className: classnames('w-4 h-4 fill-current'),
			})}
		</>
	);

	const endIcon = endIconProp && React.isValidElement(endIconProp) && (
		<>
			{React.cloneElement(endIconProp, {
				className: classnames('w-4 h-4 fill-current'),
			})}
		</>
	);
	const buttonElement = useRef(null);

	const handleOnClick = (e: React.MouseEvent) => {
		buttonElement.current.blur();
		if (!disabled) {
			onClick(e);
		}
	};

	const finalClassName = classnames(
		layoutClasses,
		fontTextClasses[type],
		disabled ? disabledClasses : enabledClasses[type],
		sizeClasses[size],
		children ? 'min-w-[32px]' : '', // minimum width for buttons with text; icon only button does NOT get a minimum width
		className,
	);

	const data = Object.fromEntries(Object.entries(rest).filter(([key]) => key.startsWith('data-')));

	return (
		<button
			className={finalClassName}
			disabled={disabled}
			ref={buttonElement}
			onClick={handleOnClick}
			data-cy={`${name}-btn`}
			{...data}
		>
			{startIcon}
			{children}
			{endIcon}
		</button>
	);
};

export default Button;
